/**
 * @fileoverview Scout Eval Store
 * @description Persists imported Scout runs in local storage and derives run-over-run
 * comparisons.
 * @module features/ScoutEval/utils/scoutStore
 */

import { MAX_SCOUT_RUNS, MOVEMENT_EPSILON, SCOUT_STORAGE_KEY } from '../constants';

/**
 * Read every stored run, oldest first.
 * @returns {Array<Object>} Stored runs.
 */
export function loadRuns() {
  try {
    const raw = window.localStorage.getItem(SCOUT_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/**
 * Replace the stored runs.
 * @param {Array<Object>} runs - Runs to persist.
 * @returns {Array<Object>} The persisted runs.
 * @throws {Error} When the browser rejects the write, usually on quota.
 */
function persist(runs) {
  try {
    window.localStorage.setItem(SCOUT_STORAGE_KEY, JSON.stringify(runs));
  } catch {
    throw new Error('Could not save this report locally — remove an older run and try again.');
  }
  return runs;
}

/**
 * Store a parsed run, replacing any earlier import of the same report.
 * @param {Object} run - Parsed run record.
 * @returns {Array<Object>} Updated runs.
 */
export function addRun(run) {
  const retained = loadRuns().filter(
    (existing) => !(existing.fileName === run.fileName && existing.generatedAt === run.generatedAt),
  );
  return persist([...retained, run].slice(-MAX_SCOUT_RUNS));
}

/**
 * Remove one run.
 * @param {string} runId - Run identifier.
 * @returns {Array<Object>} Updated runs.
 */
export function deleteRun(runId) {
  return persist(loadRuns().filter((run) => run.id !== runId));
}

/**
 * Classify the movement of a single score.
 * @param {number|null} before - Baseline score.
 * @param {number|null} after - Current score.
 * @returns {string} Movement key.
 */
function movementFor(before, after) {
  if (before === null || before === undefined) {
    return 'added';
  }
  if (after === null || after === undefined) {
    return 'removed';
  }
  const delta = after - before;
  if (delta > MOVEMENT_EPSILON) {
    return 'improved';
  }
  if (delta < -MOVEMENT_EPSILON) {
    return 'regressed';
  }
  return 'unchanged';
}

/**
 * Build a signed delta, or null when either side is missing.
 * @param {number|null} before - Baseline value.
 * @param {number|null} after - Current value.
 * @returns {number|null} Delta.
 */
function deltaFor(before, after) {
  return before === null || before === undefined || after === null || after === undefined
    ? null
    : after - before;
}

/**
 * Compare the evaluator scores and group metrics of two runs.
 * @param {Object} baseline - Earlier run.
 * @param {Object} current - Later run.
 * @returns {Object} Comparison view.
 */
export function compareRuns(baseline, current) {
  const baselineRows = new Map((baseline?.evaluators || []).map((row) => [row.key, row]));
  const currentRows = new Map((current?.evaluators || []).map((row) => [row.key, row]));

  const rows = [...new Set([...currentRows.keys(), ...baselineRows.keys()])].map((key) => {
    const before = baselineRows.get(key) || null;
    const after = currentRows.get(key) || null;
    const source = after || before;

    return {
      key,
      field: source.field,
      evaluator: source.evaluator,
      framework: source.framework,
      detailId: source.detailId || '',
      before: before?.score ?? null,
      after: after?.score ?? null,
      beforeBand: before?.band ?? 'not-run',
      afterBand: after?.band ?? 'not-run',
      delta: deltaFor(before?.score ?? null, after?.score ?? null),
      movement: movementFor(before?.score ?? null, after?.score ?? null),
    };
  }).sort((left, right) => Math.abs(right.delta ?? 0) - Math.abs(left.delta ?? 0));

  const groups = (current?.groups || []).map((group) => {
    const baselineGroup = (baseline?.groups || []).find((item) => item.id === group.id);
    const baselineMetrics = new Map((baselineGroup?.metrics || []).map((metric) => [metric.label, metric]));

    return {
      id: group.id,
      label: group.label,
      metrics: (group.metrics || []).map((metric) => {
        const before = baselineMetrics.get(metric.label)?.score ?? null;
        return {
          label: metric.label,
          before,
          after: metric.score,
          delta: deltaFor(before, metric.score),
          movement: movementFor(before, metric.score),
        };
      }),
    };
  });

  const tally = rows.reduce((counts, row) => ({ ...counts, [row.movement]: counts[row.movement] + 1 }), {
    improved: 0, regressed: 0, unchanged: 0, added: 0, removed: 0,
  });

  return {
    baseline,
    current,
    rows,
    groups,
    tally,
    summary: {
      overallAverage: {
        before: baseline?.summary?.overallAverage ?? null,
        after: current?.summary?.overallAverage ?? null,
        delta: deltaFor(baseline?.summary?.overallAverage ?? null, current?.summary?.overallAverage ?? null),
      },
      belowTarget: {
        before: baseline?.summary?.belowTarget ?? null,
        after: current?.summary?.belowTarget ?? null,
        delta: deltaFor(baseline?.summary?.belowTarget ?? null, current?.summary?.belowTarget ?? null),
      },
      evaluatorsScored: {
        before: baseline?.summary?.evaluatorsScored ?? null,
        after: current?.summary?.evaluatorsScored ?? null,
        delta: deltaFor(baseline?.summary?.evaluatorsScored ?? null, current?.summary?.evaluatorsScored ?? null),
      },
    },
  };
}

/**
 * Find the run recorded immediately before another one.
 * @param {Array<Object>} runs - Stored runs, oldest first.
 * @param {string} runId - Reference run identifier.
 * @returns {Object|null} Previous run.
 */
export function previousRun(runs, runId) {
  const index = runs.findIndex((run) => run.id === runId);
  return index > 0 ? runs[index - 1] : null;
}
